// src/components/SetReminderModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { Bell } from 'lucide-react';
import { useReminders } from '../context/ReminderContext';
import toast from 'react-hot-toast';

function formatLocalDatetime(date) {
  const pad = (n) => String(n).padStart(2, '0');
  const y = date.getFullYear();
  const m = pad(date.getMonth() + 1);
  const d = pad(date.getDate());
  const h = pad(date.getHours());
  const min = pad(date.getMinutes());
  return `${y}-${m}-${d}T${h}:${min}`;
}

export default function SetReminderModal({ show, onClose, lead }) {
  const { createReminder } = useReminders();
  const [title, setTitle]         = useState('');
  const [datetime, setDatetime]   = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      setTitle('');
      // Default datetime: 1 hour from now, rounded to next 5 minutes
      const d = new Date(Date.now() + 60 * 60 * 1000);
      d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5);
      setDatetime(formatLocalDatetime(d));
    }
  }, [show]);

  const applyPreset = (presetType) => {
    const now = new Date();
    let target = new Date();

    if (presetType === '1h') {
      target = new Date(now.getTime() + 60 * 60 * 1000);
    } else if (presetType === 'today_5pm') {
      target.setHours(17, 0, 0, 0);
      if (target <= now) target = new Date(now.getTime() + 2 * 60 * 60 * 1000);
    } else if (presetType === 'tomorrow_10am') {
      target.setDate(target.getDate() + 1);
      target.setHours(10, 0, 0, 0);
    } else if (presetType === '2d') {
      target.setDate(target.getDate() + 2);
      target.setHours(11, 0, 0, 0);
    } else if (presetType === 'next_week') {
      target.setDate(target.getDate() + 7);
      target.setHours(10, 0, 0, 0);
    }

    setDatetime(formatLocalDatetime(target));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error('Please enter a reminder description');
      return;
    }
    if (!datetime) {
      toast.error('Please select reminder date and time');
      return;
    }

    const remindAt = new Date(datetime);
    if (isNaN(remindAt.getTime())) {
      toast.error('Invalid date format');
      return;
    }

    try {
      setSubmitting(true);
      await createReminder({
        leadId: lead?.id || lead?._id,
        title: title.trim(),
        remindAt: remindAt.toISOString()
      });
      onClose();
    } catch (_) {
      // Error handled in context
    } finally {
      setSubmitting(false);
    }
  };

  if (!lead) return null;

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton style={{ background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
        <Modal.Title style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Bell size={16} style={{ color: 'var(--primary)' }} /> Set Lead Reminder
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ background: 'var(--surface)', color: 'var(--text)', padding: 22 }}>
        <div className="mb-3 p-2 rounded" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', fontSize: 13 }}>
          <div className="fw-semibold text-truncate">{lead.name || 'Lead'}</div>
          <div className="text-muted small">{lead.number || 'No phone'} · {lead.status || 'Active'}</div>
        </div>

        <Form onSubmit={handleSubmit}>
          {/* Quick Preset Buttons */}
          <div className="mb-3">
            <Form.Label className="small fw-semibold text-muted text-uppercase" style={{ letterSpacing: '0.04em', fontSize: 11 }}>
              Quick Presets
            </Form.Label>
            <div className="d-flex flex-wrap gap-2">
              {[
                { id: '1h', label: 'In 1 Hour' },
                { id: 'today_5pm', label: 'Today 5:00 PM' },
                { id: 'tomorrow_10am', label: 'Tomorrow 10:00 AM' },
                { id: '2d', label: 'In 2 Days' },
                { id: 'next_week', label: 'Next Week' },
              ].map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => applyPreset(p.id)}
                  style={{
                    background: 'var(--surface-2)',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                    borderRadius: 8,
                    padding: '4px 10px',
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.12s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--primary)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Date & Time Picker */}
          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold text-muted text-uppercase" style={{ letterSpacing: '0.04em', fontSize: 11 }}>
              Date & Time
            </Form.Label>
            <Form.Control
              type="datetime-local"
              size="sm"
              value={datetime}
              onChange={(e) => setDatetime(e.target.value)}
              style={{
                background: 'var(--surface-2)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: '8px 12px',
                fontSize: 13
              }}
              required
            />
          </Form.Group>

          {/* Reminder Description */}
          <Form.Group className="mb-4">
            <Form.Label className="small fw-semibold text-muted text-uppercase" style={{ letterSpacing: '0.04em', fontSize: 11 }}>
              Reminder Note / Task
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="e.g. Call client to discuss auction sheet and confirm budget..."
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={{
                background: 'var(--surface-2)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: '8px 12px',
                fontSize: 13
              }}
              required
            />
          </Form.Group>

          <div className="d-flex justify-content-end gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={onClose}
              disabled={submitting}
              style={{ borderRadius: 8, padding: '6px 14px', fontSize: 13 }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              type="submit"
              disabled={submitting}
              style={{ borderRadius: 8, padding: '6px 16px', fontSize: 13, fontWeight: 600 }}
            >
              {submitting ? 'Scheduling...' : 'Set Reminder'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
